'use client'

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { validateSymbol, getAvailableSymbols } from '../../../lib/firestore'
import styles from './Posts.module.css'

interface SymbolSelectorProps {
  initialSymbol?: string
  onSymbolChange: (symbol: string, ltp: number) => void
  disabled?: boolean
}

export const SymbolSelector = React.memo(({ initialSymbol, onSymbolChange, disabled }: SymbolSelectorProps) => {
  const [selectedSymbol, setSelectedSymbol] = useState(initialSymbol || '')
  const [customSymbol, setCustomSymbol] = useState('')
  const [availableSymbols, setAvailableSymbols] = useState<string[]>([])
  const [validationState, setValidationState] = useState<{
    isValidating: boolean
    isValid: boolean
    ltp?: number
    error?: string
  }>({ isValidating: false, isValid: false })
  const [showDropdown, setShowDropdown] = useState(false)

  // Load available symbols on mount
  useEffect(() => {
    const loadSymbols = async () => {
      try {
        const symbols = await getAvailableSymbols()
        setAvailableSymbols(symbols)
      } catch (error) {
        console.error('Failed to load available symbols:', error)
      }
    }
    loadSymbols()
  }, [])

  // Validate initial symbol
  useEffect(() => {
    if (initialSymbol) {
      validateSymbolAndUpdate(initialSymbol)
    }
  }, [initialSymbol])

  // Validate symbol and update state
  const validateSymbolAndUpdate = useCallback(async (symbol: string) => {
    if (!symbol.trim()) {
      setValidationState({ isValidating: false, isValid: false })
      return
    }

    setValidationState({ isValidating: true, isValid: false })
    
    try {
      const result = await validateSymbol(symbol.trim().toUpperCase())
      
      if (result.isValid && result.ltp) {
        setValidationState({ 
          isValidating: false, 
          isValid: true, 
          ltp: result.ltp 
        })
        onSymbolChange(symbol.trim().toUpperCase(), result.ltp)
      } else {
        setValidationState({ 
          isValidating: false, 
          isValid: false, 
          error: result.error || 'Symbol validation failed' 
        })
      }
    } catch (error) {
      setValidationState({ 
        isValidating: false, 
        isValid: false, 
        error: 'Failed to validate symbol' 
      })
    }
  }, [onSymbolChange])

  // Handle symbol selection from dropdown
  const handleSymbolSelect = useCallback((symbol: string) => {
    setSelectedSymbol(symbol)
    setCustomSymbol('')
    setShowDropdown(false)
    validateSymbolAndUpdate(symbol)
  }, [validateSymbolAndUpdate])

  // Handle custom symbol input
  const handleCustomSymbolChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase()
    setCustomSymbol(value)
    setSelectedSymbol('')
    
    // Debounce validation
    const timeoutId = setTimeout(() => {
      if (value.trim()) {
        validateSymbolAndUpdate(value)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [validateSymbolAndUpdate])

  // Handle custom symbol submission
  const handleCustomSymbolSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (customSymbol.trim()) {
      validateSymbolAndUpdate(customSymbol)
    }
  }, [customSymbol, validateSymbolAndUpdate])

  // Filter available symbols based on custom input
  const filteredSymbols = useMemo(() => {
    if (!customSymbol) return availableSymbols.slice(0, 10) // Show top 10
    return availableSymbols.filter(symbol => 
      symbol.includes(customSymbol.toUpperCase())
    ).slice(0, 10)
  }, [availableSymbols, customSymbol])

  const currentSymbol = selectedSymbol || customSymbol

  return (
    <div className={styles.symbolSelector}>
      <label className={styles.symbolLabel}>
        Select Symbol
      </label>
      
      <div className={styles.symbolInputContainer}>
        {/* Dropdown for existing symbols */}
        <div className={styles.symbolDropdown}>
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={disabled}
            className={styles.dropdownToggle}
          >
            {selectedSymbol || 'Select Symbol'} ▼
          </button>
          
          {showDropdown && (
            <div className={styles.dropdownMenu}>
              {availableSymbols.length > 0 ? (
                availableSymbols.map((symbol) => (
                  <button
                    key={symbol}
                    type="button"
                    onClick={() => handleSymbolSelect(symbol)}
                    className={styles.dropdownItem}
                  >
                    {symbol}
                  </button>
                ))
              ) : (
                <div className={styles.dropdownItem}>No symbols available</div>
              )}
            </div>
          )}
        </div>

        <span className={styles.orText}>OR</span>

        {/* Custom symbol input */}
        <form onSubmit={handleCustomSymbolSubmit} className={styles.customSymbolForm}>
          <input
            type="text"
            value={customSymbol}
            onChange={handleCustomSymbolChange}
            placeholder="Enter new symbol (e.g., RELIANCE, BTCUSDT)"
            disabled={disabled}
            className={styles.customSymbolInput}
          />
          
          {/* Show filtered suggestions */}
          {customSymbol && filteredSymbols.length > 0 && (
            <div className={styles.suggestions}>
              {filteredSymbols.map((symbol) => (
                <button
                  key={symbol}
                  type="button"
                  onClick={() => handleSymbolSelect(symbol)}
                  className={styles.suggestionItem}
                >
                  {symbol}
                </button>
              ))}
            </div>
          )}
        </form>
      </div>

      {/* Validation status */}
      <div className={styles.symbolStatus}>
        {validationState.isValidating && (
          <span className={styles.validating}>Validating symbol...</span>
        )}
        
        {validationState.isValid && validationState.ltp && (
          <span className={styles.valid}>
            ✅ {currentSymbol}: ₹{validationState.ltp.toFixed(2)}
          </span>
        )}
        
        {validationState.error && (
          <span className={styles.invalid}>
            ❌ {validationState.error}
          </span>
        )}
      </div>
    </div>
  )
}) 